// steps/CalculateFlexibility.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';

const CalculateFlexibility = ({
  handleBack,
  modelName,
  modelDesc,
  selectedCountry,
  selectedOption,
  selectedUserId,
  flexibilityDays,
  fileName,
  fileSize,
  selectedDevices = [],
  dataOption
}) => {
  const navigate = useNavigate();
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState(null);

  const handleCalculateFlexibility = async () => {
    setIsCalculating(true);
    setError(null);

    // If we're keeping existing data, just navigate to the dashboard
    if (dataOption === 'keep') {
      localStorage.setItem('selectedOption', selectedOption);
      localStorage.setItem('selectedUserId', selectedUserId.toString());
      localStorage.setItem('selectedCountry', selectedCountry);
      localStorage.setItem('selectedModelName', modelName);
      localStorage.setItem('modelTrainingStatus', 'completed'); // Model exists and is "trained"
      localStorage.setItem('modelTrainingError', ''); // Clear any stale errors

      const method = selectedOption.toLowerCase();
      navigate(`/dashboard/${method}/analytics`, {
        state: {
          modelName: modelName,
          modelDesc: modelDesc,
          country: selectedCountry,
          method: selectedOption,
          userId: selectedUserId,
          freshCalculation: false, // Not a fresh calculation
          modelProcessing: false, // No processing
        },
        replace: true,
      });
      setIsCalculating(false);
      return;
    }

    try {
      const isAggregated = selectedOption === 'Aggregated';
      const isDisaggregated = selectedOption === 'Disaggregated';

      // Validate required data before proceeding
      if (!modelName || !selectedCountry || !selectedOption || !selectedUserId) {
        throw new Error('Missing required configuration. Please ensure all fields are completed.');
      }

      console.log('STEP 1: Creating model...');

      // Store the selected option in localStorage for the dashboard
      localStorage.setItem('selectedOption', selectedOption);
      localStorage.setItem('selectedUserId', selectedUserId.toString());
      localStorage.setItem('selectedCountry', selectedCountry);
      localStorage.setItem('selectedModelName', modelName);

      // Set initial training status - model creation is starting
      localStorage.setItem('modelTrainingStatus', 'creating');
      localStorage.setItem('modelTrainingError', '');

      const newModel = {
        model_name: modelName,
        model_desc: modelDesc,
        country: selectedCountry,
        method: selectedOption,
        user_id: selectedUserId.toString(),
      };

      console.log('Creating model with data:', newModel);

      let modelResponse;

      // Create model based on selected method
      if (isAggregated) {
        modelResponse = await fetch('https://flexibility-backend.dedalus.epu.ntua.gr/models/create', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
          body: JSON.stringify(newModel),
        });
      } else if (isDisaggregated) {
        modelResponse = await fetch('https://flexibility-backend.dedalus.epu.ntua.gr/models/create-forecasting-disaggregation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
          body: JSON.stringify(newModel),
        });
      } else {
        throw new Error('Invalid method selected. Please select either Aggregated or Disaggregated.');
      }

      if (!modelResponse.ok) {
        const errorText = await modelResponse.text();
        console.error('Model creation failed:', {
          status: modelResponse.status,
          statusText: modelResponse.statusText,
          errorText: errorText,
          requestData: newModel
        });
        const errorMessage = `Failed to create model: ${modelResponse.status} ${modelResponse.statusText} - ${errorText}`;
        localStorage.setItem('modelTrainingStatus', 'failed');
        localStorage.setItem('modelTrainingError', errorMessage);
        throw new Error(errorMessage);
      }

      const modelData = await modelResponse.json();
      console.log('STEP 1 COMPLETED: Model created successfully:', modelData);

      // Update model name to the one returned by the API (in case it was modified)
      const finalModelName = modelData.model_name || modelName;
      localStorage.setItem('selectedModelName', finalModelName);

      // Set status to indicate model is ready for training (will be handled by UnifiedDashboard)
      localStorage.setItem('modelTrainingStatus', 'created');

      console.log('STEP 2: Navigating to dashboard...');

      // Navigate to the unified dashboard immediately
      const method = selectedOption.toLowerCase();
      console.log(`Navigating to dashboard with method: ${method}, model: ${finalModelName}`);

      console.log('flexibilityDays before navigation:', flexibilityDays);
      navigate(`/dashboard/${method}/analytics`, {
        state: {
          modelName: finalModelName,
          modelDesc: modelDesc,
          country: selectedCountry,
          method: selectedOption,
          flexibilityDays: flexibilityDays,
          selectedDevices: selectedDevices,
          userId: selectedUserId,
          // Flag to indicate this is a fresh calculation
          freshCalculation: true,
          // Flag to indicate model needs training
          modelProcessing: true,
        },
        replace: true, // Replace current history entry to prevent going back to stepper
      });

      console.log('STEP 2 COMPLETED: Navigation completed. UnifiedDashboard will handle analytics loading and model training.');

    } catch (error) {
      console.error('Error during model creation:', error);
      // Update localStorage with error status
      localStorage.setItem('modelTrainingStatus', 'failed');
      localStorage.setItem('modelTrainingError', error.message);
      setError(error.message);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <Box>
      {/* <Typography variant="h5" gutterBottom>
        Calculate Flexibility
      </Typography> */}

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review your configuration and start the flexibility calculation process.
      </Typography>

      {/* Configuration Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuration Summary
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Model Name
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {modelName || 'Not specified'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Model Description
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {modelDesc || 'Not specified'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Country
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedCountry || 'Not specified'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Method
              </Typography>
              <Chip
                label={selectedOption || 'Not specified'}
                color={selectedOption === 'Aggregated' ? 'primary' : 'secondary'}
                size="small"
              />
            </Grid>

            {selectedOption === 'Aggregated' && flexibilityDays ? (
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Flexibility Time Range
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {flexibilityDays} days
                </Typography>
              </Grid>
            ) : selectedOption === 'Disaggregated' && selectedDevices.length > 0 ? (
              <Grid item xs={12} sm={8}>
                <Typography variant="subtitle2" color="text.secondary">
                  Selected Devices
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 2 }}>
                  {selectedDevices.map((device) => (
                    <Chip
                      key={device}
                      label={device.replace('_', ' ')}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
            ) : (
              // Empty grid item to maintain spacing when no devices/days are selected
              <Grid item xs={12} sm={8} />
            )}

            {fileName && (
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Uploaded File
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {fileName}
                </Typography>
              </Grid>
            )}

            {fileSize && (
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  File Size
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {fileSize.toFixed(2)} MB
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <Button
          onClick={handleBack}
          variant="outlined"
          size="large"
          disabled={isCalculating}
        >
          Back
        </Button>
        <Button
          onClick={handleCalculateFlexibility}
          variant="contained"
          color="primary"
          size="large"
          sx={{ ml: 2, px: 4 }}
          disabled={isCalculating}
          startIcon={isCalculating ? <CircularProgress size={20} /> : null}
        >
          {isCalculating ? 'Starting Process...' : 'CALCULATE'}
        </Button>
      </Box>

      {/* Additional info about what happens when clicking the button */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
        This will redirect you to the {selectedOption} dashboard and start creating and training your model in the background.
        Analytics data will load immediately, while forecasts data will load automatically once model training is completed.
      </Typography>
    </Box>
  );
};

export default CalculateFlexibility;
